import { Injectable, NgZone } from '@angular/core'
import { BehaviorSubject } from 'rxjs'

export type MicTestCallback = (levelRms01: number) => void

@Injectable({ providedIn: 'root' })
export class AudioDevicesService {
  private micsSubject = new BehaviorSubject<MediaDeviceInfo[]>([])
  readonly mics$ = this.micsSubject.asObservable()

  private speakersSubject = new BehaviorSubject<MediaDeviceInfo[]>([])
  readonly speakers$ = this.speakersSubject.asObservable()

  // プラットフォーム判定
  readonly isAndroid =
    typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent)

  private selectedMicIdSubject = new BehaviorSubject<string>(
    localStorage.getItem('micId') ?? '',
  )
  readonly selectedMicId$ = this.selectedMicIdSubject.asObservable()

  private selectedSpeakerIdSubject = new BehaviorSubject<string>(
    localStorage.getItem('spkId') ?? '',
  )
  readonly selectedSpeakerId$ = this.selectedSpeakerIdSubject.asObservable()

  readonly sinkCapable: boolean =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'setSinkId' in (HTMLMediaElement.prototype as any)

  private locked = false

  private micPermissionDeniedSubject = new BehaviorSubject<boolean>(false)
  readonly micPermissionDenied$ = this.micPermissionDeniedSubject.asObservable()

  private permissionErrorMsgSubject = new BehaviorSubject<string>('')
  readonly permissionErrorMsg$ = this.permissionErrorMsgSubject.asObservable()

  constructor(private zone: NgZone) {
    this.refreshDevices()

    navigator.mediaDevices?.addEventListener?.('devicechange', () =>
      this.refreshDevices(),
    )
  }

  lockSelections(): void {
    this.locked = true
  }
  unlockSelections(): void {
    this.locked = false
  }

  async ensureLabels(): Promise<boolean> {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true })
      s.getTracks().forEach((t) => t.stop())
      this.micPermissionDeniedSubject.next(false)
      this.permissionErrorMsgSubject.next('')
      return true
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      let msg = 'マイクの権限がありません。ブラウザ設定から許可してください。'
      if (e?.name === 'NotFoundError') {
        msg = 'マイクデバイスが見つかりません。接続を確認してください。'
      } else if (e?.name === 'NotReadableError') {
        msg = '別のアプリがマイクを使用中です。閉じてから再度お試しください。'
      }
      this.micPermissionDeniedSubject.next(true)
      this.permissionErrorMsgSubject.next(msg)
      return false
    } finally {
      await this.refreshDevices()
    }
  }

  async refreshDevices(): Promise<void> {
    const all = await navigator.mediaDevices.enumerateDevices()

    const mics = this.sanitizeDevices(all, 'audioinput')
    const spks = this.sanitizeDevices(all, 'audiooutput')

    this.micsSubject.next(mics)
    this.speakersSubject.next(spks)

    const curMic = this.selectedMicIdSubject.value
    if (curMic && !mics.some((d) => d.deviceId === curMic)) {
      this._setMicInternal('')
    }
    const curSpk = this.selectedSpeakerIdSubject.value
    if (curSpk && !spks.some((d) => d.deviceId === curSpk)) {
      this._setSpeakerInternal('')
    }
  }

  // 追加: 重複排除＆論理デバイス(default/communications)の除外
  private sanitizeDevices(
    all: MediaDeviceInfo[],
    kind: MediaDeviceKind,
  ): MediaDeviceInfo[] {
    const candidates = all
      .filter((d) => d.kind === kind)
      // 既定(=default) と 通話既定(=communications) は除外
      .filter(
        (d) => d.deviceId !== 'default' && d.deviceId !== 'communications',
      )

    const seen = new Map<string, MediaDeviceInfo>()
    for (const d of candidates) {
      const key = d.groupId || d.label || d.deviceId
      if (!seen.has(key)) seen.set(key, d)
    }
    return [...seen.values()]
  }

  setMic(deviceId: string): void {
    if (this.locked) {
      console.warn(
        '[AudioDevicesService] Device selection is locked during DIALOG.',
      )
      return
    }
    this._setMicInternal(deviceId)
  }
  private _setMicInternal(deviceId: string): void {
    localStorage.setItem('micId', deviceId)
    this.selectedMicIdSubject.next(deviceId)
  }

  setSpeaker(deviceId: string): void {
    if (this.locked) {
      console.warn(
        '[AudioDevicesService] Device selection is locked during DIALOG.',
      )
      return
    }
    this._setSpeakerInternal(deviceId)
  }
  private _setSpeakerInternal(deviceId: string): void {
    localStorage.setItem('spkId', deviceId)
    this.selectedSpeakerIdSubject.next(deviceId)
  }

  // audio要素の出力デバイスを、指定デバイス（または既定）に変更する
  async applySinkId(
    audioEl: HTMLAudioElement,
    deviceId: string,
  ): Promise<void> {
    if (!this.sinkCapable) return
    await audioEl.setSinkId(deviceId || 'default')
  }

  base64ToBlob(b64: string, mime: string): Blob {
    const byteStr = atob(b64)
    const len = byteStr.length
    const u8 = new Uint8Array(len)
    for (let i = 0; i < len; i++) u8[i] = byteStr.charCodeAt(i)
    return new Blob([u8], { type: mime })
  }

  async ensureOutputPermission(preferId: string): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyMD = navigator.mediaDevices as any
    if (!anyMD || typeof anyMD.selectAudioOutput !== 'function') {
      console.info('selectAudioOutput is not supported in this browser.')
      return
    }

    try {
      const sink: MediaDeviceInfo = await anyMD.selectAudioOutput(
        preferId ? { deviceId: preferId } : {},
      )

      if (sink?.deviceId) {
        this._setSpeakerInternal(sink.deviceId)
      }
    } catch (err) {
      console.warn('selectAudioOutput denied or cancelled:', err)
    }
  }

  // デバイス情報を取得（ログ送信用）
  getDeviceInfo(
    micId: string | null,
    speakerId: string | null,
  ): {
    platform: string
    userAgent: string
    selectedMicId: string | null
    selectedMicLabel: string | null
    selectedSpeakerId: string | null
    selectedSpeakerLabel: string | null
    isExternalMic: boolean
  } {
    const mics = this.micsSubject.value
    const speakers = this.speakersSubject.value

    const selectedMic = mics.find((m) => m.deviceId === micId)
    const selectedSpeaker = speakers.find((s) => s.deviceId === speakerId)

    const isExternalMic = selectedMic
      ? !selectedMic.label.toLowerCase().includes('internal') &&
        !selectedMic.label.toLowerCase().includes('built-in') &&
        !selectedMic.label.toLowerCase().includes('内蔵')
      : false

    return {
      platform: this.detectPlatform(),
      userAgent: navigator.userAgent,
      selectedMicId: micId || null,
      selectedMicLabel: selectedMic?.label || null,
      selectedSpeakerId: speakerId || null,
      selectedSpeakerLabel: selectedSpeaker?.label || null,
      isExternalMic,
    }
  }

  private detectPlatform(): string {
    const ua = navigator.userAgent.toLowerCase()
    if (/iphone|ipad|ipod/.test(ua)) return 'iOS'
    if (/android/.test(ua)) return 'Android'
    if (/windows/.test(ua)) return 'Windows'
    if (/macintosh|mac os x/.test(ua)) return 'macOS'
    return 'Unknown'
  }
}
